import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../constants/documentTheme';
import { formatPlanLimit, planNameKey } from '../utils/partnerPlan';
import { introductionFeeCopyVars } from '../config/plans';

const GOLD_CHIP_SX = {
  background: 'linear-gradient(180deg, #F5E6B8 0%, #D4AF37 48%, #B8860B 100%)',
  color: '#3D2A00',
  fontWeight: 700,
  border: '1px solid #E8C547',
};

const NAV_BADGE_SX = {
  mt: 0.4,
  px: 0.7,
  py: '1px',
  fontFamily: DOCUMENT.sans,
  fontSize: 8,
  fontWeight: 500,
  letterSpacing: '0.12em',
  lineHeight: 1.45,
  textTransform: 'uppercase',
  textDecoration: 'none',
  color: '#C9A961',
  border: '1px solid rgba(201,169,97,0.55)',
  bgcolor: 'transparent',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  '&:hover': {
    color: DOCUMENT.ivory,
    borderColor: '#C9A961',
  },
};

const PartnerPlanBadge = ({ entitlements, variant = 'compact', onClick, href }) => {
  const { t } = useTranslation();

  if (!entitlements) return null;

  const planName = t(planNameKey(entitlements.tier));
  const highlight = entitlements.tier === 'pro' || entitlements.tier === 'agency' || entitlements.foundingPartner;

  if (variant === 'nav') {
    const interactive = Boolean(href || onClick);
    return (
      <Box
        component={href ? RouterLink : onClick ? 'button' : 'span'}
        to={href}
        onClick={onClick}
        data-testid="partner-nav-plan"
        aria-label={`${t('partnerPlans.currentPlan')}: ${planName}`}
        sx={{
          ...NAV_BADGE_SX,
          cursor: interactive ? 'pointer' : 'default',
          ...(onClick && !href ? { display: 'inline-block' } : {}),
        }}
      >
        {planName}
      </Box>
    );
  }

  return (
    <Stack
      spacing={variant === 'detailed' ? 0.75 : 0.5}
      alignItems={variant === 'detailed' ? 'flex-start' : 'center'}
    >
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent="center">
        <Chip
          label={planName}
          size="small"
          onClick={onClick}
          sx={{
            fontWeight: 700,
            cursor: onClick ? 'pointer' : 'default',
            ...(highlight ? GOLD_CHIP_SX : { bgcolor: '#f4f1ef', color: '#3D2A00' }),
          }}
        />
        {entitlements.status === 'trialing' && (
          <Chip label={t('partnerPlans.trialing')} size="small" color="info" />
        )}
        {entitlements.foundingPartner && (
          <Chip label={t('partnerPlans.founding')} size="small" color="success" />
        )}
      </Stack>
      {variant === 'detailed' && entitlements.scheduledPlan && (
        <Typography variant="caption" color="text.secondary">
          {t('partnerPlans.scheduledBadge', {
            plan: entitlements.scheduledPlan.planName,
            date: entitlements.scheduledPlan.effectiveAt
              ? new Date(String(entitlements.scheduledPlan.effectiveAt).replace(' ', 'T')).toLocaleDateString()
              : ''
          })}
        </Typography>
      )}
      {variant === 'detailed' && (
        <>
          <Typography variant="caption" color="text.secondary">
            {t('partnerPlans.teamSeats', {
              used: entitlements.usage?.teamMembers ?? 1,
              limit: formatPlanLimit(
                entitlements.limits?.includedTeamSeats ?? entitlements.limits?.maxTeamMembers,
                t('partnerPlans.unlimited')
              )
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('partnerPlans.feature.introductionFee', { rate: entitlements.introductionFeeRate ?? 0, ...introductionFeeCopyVars() })}
          </Typography>
        </>
      )}
    </Stack>
  );
};

export default PartnerPlanBadge;
